import {
  toAccordionContent,
  toBannerContent,
  toCallToActionContent,
  toCardGridContent,
  toIframeContent,
  toStepsContent,
  toVideoContent,
} from "./blockContent";
import { BLOCK_LINK_ERROR, isHttpsUrl, isValidBlockLink } from "./blockLink";
import type { BlockButton, BlockContentErrors } from "./types";
import { videoProvider } from "./videoUrl";

export const ANCHOR_ID_FORMAT_ERROR = "Use lowercase letters, numbers and hyphens only";

export const ANCHOR_ID_TAKEN_ERROR = "Another block on this page already uses this anchor id";

const ALT_TEXT_ERROR = "Describe the image for people who can't see it";

const ANCHOR_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const HEADING_TYPES = new Set([
  "block_card_grid",
  "block_iframe",
  "block_text",
  "block_stats",
  "block_people",
  "block_icon_list",
  "block_gallery",
  "block_banner",
  "block_steps",
  "block_video",
  "block_accordion",
  "block_call_to_action",
]);

const INTRO_TYPES = new Set([
  "block_card_grid",
  "block_iframe",
  "block_people",
  "block_icon_list",
  "block_gallery",
  "block_steps",
  "block_video",
]);

const BACKGROUND_TYPES = new Set([...HEADING_TYPES, "block_hero", "block_buttons"]);

const ANCHOR_ID_TYPES = new Set([
  "block_text",
  "block_banner",
  "block_steps",
  "block_video",
  "block_accordion",
  "block_call_to_action",
]);

const KEPT_FIELDS: Array<[string, Set<string>]> = [
  ["heading", HEADING_TYPES],
  ["intro", INTRO_TYPES],
  ["background", BACKGROUND_TYPES],
  ["anchorId", ANCHOR_ID_TYPES],
];

type RawContent = Record<string, unknown> | null;

function isBlank(value: string | undefined): boolean {
  return (value ?? "").trim() === "";
}

export function startingContent(
  blockType: string,
  defaultContent: Record<string, unknown>,
  current: RawContent,
): Record<string, unknown> {
  if (current === null) {
    return defaultContent;
  }
  const kept = KEPT_FIELDS.filter(
    ([field, types]) => types.has(blockType) && current[field] !== undefined,
  ).map(([field]) => [field, current[field]]);
  return { ...defaultContent, ...Object.fromEntries(kept) };
}

export function readAnchorId(content: RawContent): string | null {
  const anchorId = content?.anchorId;
  return typeof anchorId === "string" && anchorId.trim() !== "" ? anchorId.trim() : null;
}

function validateAnchorId(
  content: RawContent,
  otherAnchorIds: string[],
  errors: BlockContentErrors,
) {
  const anchorId = readAnchorId(content);
  if (anchorId === null) {
    return;
  }
  if (!ANCHOR_ID.test(anchorId)) {
    errors.anchorId = ANCHOR_ID_FORMAT_ERROR;
  } else if (otherAnchorIds.includes(anchorId)) {
    errors.anchorId = ANCHOR_ID_TAKEN_ERROR;
  }
}

function requireText(
  value: string | undefined,
  path: string,
  message: string,
  errors: BlockContentErrors,
) {
  if (isBlank(value)) {
    errors[path] = message;
  }
}

function requireAltText(
  image: string | undefined,
  alt: string | undefined,
  path: string,
  errors: BlockContentErrors,
) {
  if (!isBlank(image)) {
    requireText(alt, path, ALT_TEXT_ERROR, errors);
  }
}

function isButtonSet(button: BlockButton | undefined): boolean {
  return !isBlank(button?.label) || !isBlank(button?.href);
}

function validateButton(
  button: BlockButton | undefined,
  path: string,
  required: boolean,
  errors: BlockContentErrors,
) {
  if (!required && !isButtonSet(button)) {
    return;
  }
  requireText(button?.label, `${path}.label`, "Enter a button label", errors);
  const href = button?.href.trim() ?? "";
  if (href === "") {
    errors[`${path}.href`] = "Enter a link";
  } else if (!isValidBlockLink(href)) {
    errors[`${path}.href`] = BLOCK_LINK_ERROR;
  }
}

export function validateBlockContent(
  blockType: string,
  content: RawContent,
  otherAnchorIds: string[],
): BlockContentErrors {
  const errors: BlockContentErrors = {};

  if (ANCHOR_ID_TYPES.has(blockType)) {
    validateAnchorId(content, otherAnchorIds, errors);
  }

  switch (blockType) {
    case "block_banner": {
      const banner = toBannerContent(content);
      requireText(banner.title, "title", "Enter a title", errors);
      validateButton(banner.primaryButton, "primaryButton", false, errors);
      validateButton(banner.secondaryButton, "secondaryButton", false, errors);
      if (!isBlank(banner.videoUrl)) {
        if (videoProvider((banner.videoUrl ?? "").trim()) !== "vimeo") {
          errors.videoUrl = "Use a Vimeo video link";
        }
        requireText(banner.poster, "poster", "Add a poster image for the background video", errors);
      }
      break;
    }

    case "block_steps":
      toStepsContent(content).items.forEach((item, index) =>
        requireAltText(item.image, item.imageAlt, `items.${index}.imageAlt`, errors),
      );
      break;

    case "block_video": {
      const video = toVideoContent(content);
      if (isBlank(video.url)) {
        errors.url = "Enter a Vimeo or YouTube link";
      } else if (videoProvider(video.url.trim()) === null) {
        errors.url = "Use a Vimeo or YouTube video link";
      }
      requireText(video.title, "title", "Enter a title", errors);
      break;
    }

    case "block_accordion":
      toAccordionContent(content).items.forEach((item, index) =>
        requireText(item.question, `items.${index}.question`, "Enter a question", errors),
      );
      break;

    case "block_call_to_action": {
      const callToAction = toCallToActionContent(content);
      if (callToAction.buttons.length === 0) {
        errors.buttons = "Add at least one button";
      }
      callToAction.buttons.forEach((button, index) =>
        validateButton(button, `buttons.${index}`, true, errors),
      );
      break;
    }

    case "block_iframe": {
      const iframe = toIframeContent(content);
      const src = iframe.src.trim();
      if (src === "") {
        errors.src = "Enter a source URL";
      } else if (!isHttpsUrl(src)) {
        errors.src = "Use an https:// link";
      }
      requireText(iframe.title, "title", "Enter a title", errors);
      break;
    }

    case "block_card_grid": {
      const cardGrid = toCardGridContent(content);
      if (cardGrid.cardStyle !== "text") {
        cardGrid.cards.forEach((card, index) =>
          requireAltText(card.image, card.imageAlt, `cards.${index}.imageAlt`, errors),
        );
      }
      break;
    }
  }

  return errors;
}
